import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material/';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import { formatSignedCash } from '../../../utils/formatCash';

// Fixed height so switching tabs cannot make the modal jump: the shift has
// fewer products than the day, and resizing on every tap reads as a glitch
const TABLE_HEIGHT = '46vh';

// Enough to fill 46vh so the table does not visibly grow when the data lands
const SKELETON_ROWS = 8;

function ModalKioskProducts({ onBack }) {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);
  const { hall } = useContext(AppContext);

  // 0 shift · 1 day — the shift is what the person in front of the till owns
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handler = (response) => {
      if (!response || response.ok === false) {
        setFailed(true);
        return;
      }
      setFailed(false);
      setData(response);
      // No open shift: the day is the only thing there is to show
      if (!response.shift) setTab(1);
    };

    socket.on('kioskProducts', handler);
    socket.emit('kioskProducts', { bingoName: hall });
    return () => socket.off('kioskProducts', handler);
  }, [socket, hall]);

  const section = tab === 0 ? data?.shift : data?.day;

  const headerCellSx = {
    color: '#8fa3d4 !important',
    fontSize: '17px',
    fontWeight: 700,
  };
  const bodyCellSx = { color: '#e6eaf2 !important', fontSize: '19px' };

  return (
    <>
      <h1>{t('sales.kioskProductsTitle')}</h1>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          mb: 1,
          '& .MuiTab-root': { color: '#8fa3d4', fontSize: '17px' },
          '& .Mui-selected': { color: '#fcad89 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#fcad89' },
        }}
      >
        <Tab label={t('sales.kioskProductsShift')} disabled={!data?.shift} />
        <Tab label={t('sales.kioskProductsDay')} />
      </Tabs>

      <TableContainer
        sx={{
          height: TABLE_HEIGHT,
          overflowY: 'auto',
          backgroundColor: '#151d3b',
          borderRadius: '14px',
          border: '1px solid #2a3a6e',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, backgroundColor: '#151d3b' }}>
                {t('sales.kioskProductsProduct')}
              </TableCell>
              <TableCell
                align="right"
                sx={{ ...headerCellSx, backgroundColor: '#151d3b' }}
              >
                {t('sales.kioskProductsQty')}
              </TableCell>
              <TableCell
                align="right"
                sx={{ ...headerCellSx, backgroundColor: '#151d3b' }}
              >
                {t('sales.kioskProductsTotal')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {failed && (
              <TableRow>
                <TableCell colSpan={3} sx={bodyCellSx}>
                  {t('error.error')}
                </TableCell>
              </TableRow>
            )}
            {/* Skeleton rows rather than the shared TableLoader: that one is
                fixed at 8 columns with its own headers and would replace this
                table whole. Same MUI Skeleton, shaped to these three columns. */}
            {!failed &&
              !data &&
              Array.from(new Array(SKELETON_ROWS)).map((_, row) => (
                <TableRow key={row}>
                  {[0, 1, 2].map((col) => (
                    <TableCell key={col} align={col === 0 ? 'left' : 'right'}>
                      <Skeleton
                        variant="text"
                        height={30}
                        width={col === 0 ? '60%' : '40%'}
                        sx={{
                          bgcolor: '#232a45',
                          ml: col === 0 ? 0 : 'auto',
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!failed && data && !section?.items?.length && (
              <TableRow>
                <TableCell colSpan={3} sx={bodyCellSx}>
                  {t('sales.kioskProductsEmpty')}
                </TableCell>
              </TableRow>
            )}
            {section?.items?.map((item) => (
              <TableRow key={item.product}>
                <TableCell sx={bodyCellSx}>
                  {item.product}
                  {/* Cancels are netted out of qty; showing them keeps that visible */}
                  {item.cancelled > 0 && (
                    <Typography sx={{ color: '#8fa3d4', fontSize: '14px' }}>
                      {t('sales.kioskProductsCancelled', {
                        count: item.cancelled,
                      })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}
                >
                  {item.qty}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatSignedCash(item.total)} kr
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 1.5,
          px: 1,
        }}
      >
        <Typography sx={{ color: '#8fa3d4', fontSize: '17px' }}>
          {t('sales.kioskProductsCount', { count: section?.qty ?? 0 })}
        </Typography>
        <Typography
          sx={{
            color: '#e6eaf2',
            fontSize: '26px',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatSignedCash(section?.total ?? 0)} kr
        </Typography>
      </Box>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
        <button className="btn-custom-no" onClick={onBack}>
          <ArrowBackIcon />
          {t('sales.openShiftBack')}
        </button>
      </div>
    </>
  );
}

export default ModalKioskProducts;
